import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SucursalVenta } from '../services/sucursalesVentaService';
import { useSucursalesUsuarioContext } from '../contexts/SucursalesUsuarioContext';
import { storedNullableNumber } from '../utils/tesoreriaFiltrosStorage';
import {
  clearGlobalNroSuc,
  persistGlobalNroSuc,
  readGlobalNroSuc,
} from '../utils/nroSucPreferida';

export {
  clearGlobalNroSuc,
  persistGlobalNroSuc,
  readGlobalNroSuc,
} from '../utils/nroSucPreferida';

export interface UseSucursalesVentaFiltroOptions {
  /** Valor inicial desde storage de página (solo si resetPreferidaOnNavigation es false). */
  initialNroSuc?: number | null;
  /**
   * Tesorería: al entrar a la pantalla aplica siempre la preferida (como formularios de alta).
   * La elección manual del filtro vale solo durante la sesión actual en esa pantalla.
   */
  resetPreferidaOnNavigation?: boolean;
}

function normalizeFiltroOptions(
  arg?: number | null | UseSucursalesVentaFiltroOptions,
): UseSucursalesVentaFiltroOptions {
  if (arg !== null && typeof arg === 'object') {
    return arg;
  }
  return { initialNroSuc: arg };
}

/**
 * Valor inicial del filtro desde storage de página (modo legacy / ventas).
 * En tesorería usar resetPreferidaOnNavigation en lugar de restaurar desde storage.
 */
export function resolveInitialFiltroNroSuc(
  stored: object = {},
  key = 'filtroNroSuc',
): number | null | undefined {
  const record = stored as Partial<Record<string, unknown>>;
  if (record.filtroNroSucTodas === true) {
    return null;
  }
  const value = storedNullableNumber(record, key, null);
  if (value != null && value > 0) {
    return value;
  }
  return undefined;
}

/** @deprecated No persistir filtroNroSuc en tesorería cuando se usa resetPreferidaOnNavigation. */
export function buildFiltroNroSucPersistFields(
  _filtroNroSuc: number | null,
  _filtroNroSucTodasExplicito: boolean,
): Record<string, never> {
  return {};
}

export function useSucursalesVentaFiltro(
  optionsArg?: number | null | UseSucursalesVentaFiltroOptions,
) {
  const { initialNroSuc: initialFromOptions, resetPreferidaOnNavigation = false } =
    normalizeFiltroOptions(optionsArg);
  const effectiveInitial = resetPreferidaOnNavigation ? undefined : initialFromOptions;

  const {
    sucursales: sucursalesUsuario,
    preferidaNro,
    loading: loadingSucursalesUsuario,
  } = useSucursalesUsuarioContext();
  const location = useLocation();

  const [filtroNroSuc, setFiltroNroSucState] = useState<number | null>(() =>
    effectiveInitial !== undefined ? effectiveInitial : null,
  );
  const [filtroListo, setFiltroListo] = useState(() => effectiveInitial !== undefined);
  // Listados: todas las asignadas. El filtro por compañía es solo para altas.
  const sucursales: SucursalVenta[] = sucursalesUsuario;
  const loadingSucursales = loadingSucursalesUsuario;
  const inicialResuelto = useRef(effectiveInitial !== undefined);
  const usuarioEligioTodasRef = useRef(effectiveInitial === null);
  const edicionManualRef = useRef(false);

  // Tesorería: al navegar a la pantalla, reiniciar filtro y volver a aplicar preferida.
  useEffect(() => {
    if (!resetPreferidaOnNavigation) return;
    edicionManualRef.current = false;
    usuarioEligioTodasRef.current = false;
    inicialResuelto.current = false;
    setFiltroNroSucState(null);
    setFiltroListo(false);
  }, [resetPreferidaOnNavigation, location.key]);

  useEffect(() => {
    if (inicialResuelto.current) {
      if (!loadingSucursales) setFiltroListo(true);
      return;
    }
    if (loadingSucursales) return;

    const inList = (n: number) =>
      Number.isFinite(n) && n > 0 && sucursales.some((s) => s.nroSuc === n);

    if (preferidaNro != null && inList(preferidaNro)) {
      inicialResuelto.current = true;
      setFiltroNroSucState(preferidaNro);
      persistGlobalNroSuc(preferidaNro);
      setFiltroListo(true);
      return;
    }
    if (sucursales.length === 0) return;

    const stored = readGlobalNroSuc();
    if (stored != null && inList(stored)) {
      inicialResuelto.current = true;
      setFiltroNroSucState(stored);
      setFiltroListo(true);
      return;
    }

    inicialResuelto.current = true;
    setFiltroListo(true);
  }, [loadingSucursales, sucursales, preferidaNro, resetPreferidaOnNavigation, location.key]);

  // Mantener sucursal válida en la lista; null = "Todas"
  useEffect(() => {
    if (loadingSucursales || sucursales.length === 0) return;
    if (resetPreferidaOnNavigation && edicionManualRef.current) return;

    const inList = (n: number) =>
      Number.isFinite(n) && n > 0 && sucursales.some((s) => s.nroSuc === n);

    setFiltroNroSucState((current) => {
      if (usuarioEligioTodasRef.current && current == null) return current;
      if (current != null && inList(current)) return current;
      if (preferidaNro != null && inList(preferidaNro)) return preferidaNro;
      const stored = readGlobalNroSuc();
      if (stored != null && inList(stored)) return stored;
      return current;
    });
  }, [loadingSucursales, sucursales, preferidaNro, resetPreferidaOnNavigation, location.key]);

  const setFiltroNroSuc = useCallback((value: number | null) => {
    edicionManualRef.current = true;
    inicialResuelto.current = true;
    usuarioEligioTodasRef.current = value == null;
    setFiltroNroSucState(value);
    setFiltroListo(true);
    if (value != null && value > 0) {
      persistGlobalNroSuc(value);
    } else {
      clearGlobalNroSuc();
    }
  }, []);

  return {
    filtroNroSuc,
    setFiltroNroSuc,
    sucursales,
    loadingSucursales,
    filtroListo,
  };
}

export function resolveNroSucFiltro(nroSuc: number | null): number | undefined {
  return nroSuc != null && nroSuc > 0 ? nroSuc : undefined;
}

export const sucursalVentaSelectOptions = (sucursales: SucursalVenta[]) =>
  sucursales.map((s) => ({
    value: s.nroSuc,
    label: s.nombreSucursal?.trim()
      ? `${String(s.nroSuc).padStart(5, '0')} — ${s.nombreSucursal.trim()}`
      : String(s.nroSuc).padStart(5, '0'),
  }));
